<template>
  <a-modal v-model:visible="dialogShow"
           width="75vh"
           class="address-modal"
           style="top:23%;"
           :title="props.mode == 'edit' ? $t('orderInfo.AddressModel.modifyAddress') : $t('orderInfo.AddressModel.newAddress')"
           @cancel="formCancel">
    <a-form ref="formRef"
            :model="formState"
            :rules="formRules"
            name="AddForm">
      <a-form-item :label-col="{ span: 5 }"
                   :wrapper-col="{ span: 18 }">
        <a-row>
          <a-col :span="12">
            <a-form-item :label="$t('orderInfo.AddressModel.contactPersonName')"
                         :colon="false"
                         :label-col="{ span: 11 }"
                         :wrapper-col="{ span: 13 }"
                         name="name">
              <a-input v-model:value="formState.name"
                       :placeholder="$t('orderInfo.AddressModel.enter')" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('orderInfo.AddressModel.contactPersonPhoneNumber')"
                         :colon="false"
                         :label-col="{ span: 12 }"
                         :wrapper-col="{ span: 14 }"
                         name="phone">
              <a-input v-model:value="formState.phone"
                       :placeholder="$t('orderInfo.AddressModel.enter')" />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form-item>
      <a-form-item :label="$t('orderInfo.AddressModel.province')"
                   class="address-model-itemLabel"
                   :label-col="{ span: 5 }"
                   :wrapper-col="{ span: 22 }"
                   :colon="false"
                   name="province">
        <a-row>
          <a-col :span="12">
            <a-form-item style="width:95%">
              <a-cascader v-model:value="formState.province.name"
                          :options="provinceArray"
                          :field-names="cascaderFieldNames"
                          :placeholder="$t('orderInfo.AddressModel.select')"
                          @dropdownVisibleChange="initCascader"
                          @change="(value: string[], selectedValue: AddressSite[]) => pickDivisionChange(value, selectedValue, 'province')" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item style="width:95%">
              <a-cascader v-model:value="formState.city.name"
                          :options="cityArray"
                          :field-names="cascaderFieldNames"
                          :placeholder="$t('orderInfo.AddressModel.select')"
                          @change="(value: string[], selectedValue: AddressSite[]) => pickDivisionChange(value, selectedValue, 'city')" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item style="width:95%">
              <a-cascader v-model:value="formState.district.name"
                          :options="districtArray"
                          :field-names="cascaderFieldNames"
                          :placeholder="$t('orderInfo.AddressModel.select')"
                          @change="(value: string[], selectedValue: AddressSite[]) => pickDivisionChange(value, selectedValue, 'district')" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item style="width:95%">
              <a-cascader v-model:value="formState.street.name"
                          :options="streetArray"
                          :field-names="cascaderFieldNames"
                          :placeholder="$t('orderInfo.AddressModel.select')"
                          @change="(value: string[], selectedValue: AddressSite[]) => pickDivisionChange(value, selectedValue, 'street')" />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form-item>
      <a-form-item :label="$t('orderInfo.AddressModel.detailedAddress')"
                   class="address-model-itemLabel2"
                   :colon="false"
                   :label-col="{ span: 5 }"
                   :wrapper-col="{ span: 24 }"
                   name="address">
        <a-row>
          <a-col :span="24">
            <a-textarea v-model:value="formState.address"
                        :placeholder="$t('orderInfo.AddressModel.enter')"
                        style="height:64px" />
          </a-col>
        </a-row>
      </a-form-item>
    </a-form>
    <template #footer>
      <div class="footerLeft">
        <a-checkbox v-model:checked="formState.default"
                    @change="defaultAddressOnChange">{{ $t('orderInfo.AddressModel.setAsDefaultAddress') }}</a-checkbox>
      </div>
      <a-button key="back"
                class="backBtn"
                @click="formCancel">{{ $t('orderInfo.AddressModel.cancel') }}</a-button>
      <a-button key="submit"
                type="primary"
                @click="formSubmit">{{ $t('orderInfo.AddressModel.save') }}</a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">

import { message } from 'ant-design-vue'
import { computed, onMounted, reactive, ref } from 'vue'
import { addressAdd, addressGetLocation, addressUpdate } from '../api/address'
import type { AddressModel, AddressSite } from '../api/interface/AddressInterface'
import { useI18n } from "vue-i18n"
const { t } = useI18n()
const props = defineProps<{ state: boolean, address: AddressModel, mode: 'edit' | 'create' }>()
const emits = defineEmits(['modalController', 'refresh'])
const formRef = ref()
const provinceArray = ref<AddressSite[]>()
const cityArray = ref<AddressSite[]>()
const districtArray = ref<AddressSite[]>()
const streetArray = ref<AddressSite[]>()
const formState = reactive<AddressModel>({
  id: 0,
  name: '',
  phone: '',
  province: {
    code: '',
    name: ''
  },
  city: {
    code: '',
    name: ''
  },
  default: false,
  district: {
    code: '',
    name: ''
  },
  street: {
    code: '',
    name: ''
  },
  address: ''
})

onMounted(async () => {
  if (props.mode != 'create') {
    const address = JSON.parse(JSON.stringify(props.address))
    Object.assign(formState, address)
    midwareCity(formState)
  } else {
    Object.assign(formState, {})
  }
})

const cascaderFieldNames = {
  label: 'name',
  value: 'code'
}

const formRules = computed(() => ({
  name: [{ type: 'string', required: true, message: t('orderInfo.AddressModel.pleaseEnterName'), trigger: 'blur' }],
  province: [{ required: true, message: t('orderInfo.AddressModel.addressInformation'), trigger: 'change' }, {
    validator() {
      const { province, city, district } = formState
      if (province.code == '' || province.code == null) {
        return Promise.reject(t('orderInfo.AddressModel.selectProvince'))
      } else if (city.code == '' || city.code == null) {
        return Promise.reject(t('orderInfo.AddressModel.selectCity'))
      } else if (district.code == '' || district.code == null) {
        return Promise.reject(t('orderInfo.AddressModel.selectDistrict'))
      } else {
        return Promise.resolve()
      }
    }
  }],
  address: [{ type: 'string', required: false, message: t('orderInfo.AddressModel.enterDetailedAddress'), trigger: 'blur' }],
  phone: [{ type: 'string', required: true, trigger: 'blur' }, {
    validator(rule: string, value: string) {
      if (typeof (value) === 'undefined' || value === '') {
        return Promise.resolve()
      } else {
        if (!testMobile(value)) {
          return Promise.reject(t('orderInfo.AddressModel.enterPhone'))
        }
        return Promise.resolve()
      }
    }
  }]
}))
const dialogShow = computed({
  get: () => props.state,
  set: () => ''
})

// declared Method
const getFullAddress = () => {
  addressGetLocation().then(result => {
    const { divisions } = result
    provinceArray.value = [...divisions]
  })
}

const initCascader = (value: boolean) => {
  if (value == true) {
    getFullAddress()
  }
}

const pickDivisionChange = (value: string[], selectedValues: AddressSite[], state: 'province' | 'city' | 'district' | 'street'): void => {
  const { code, name } = selectedValues[0]
  addressGetLocation(code).then(result => {
    const { divisions } = result
    switch (state) {
      case 'province':
        cityArray.value = [...divisions]
        formState.province = {
          code, name
        }
        formState.city = {
          code: '', name: ''
        }
        formState.district = {
          code: '', name: ''
        }
        formState.street = {
          code: '', name: ''
        }
        break
      case 'city':
        districtArray.value = [...divisions]
        formState.city = { code, name }
        formState.district = {
          code: '', name: ''
        }
        formState.street = {
          code: '', name: ''
        }
        break
      case 'district':
        streetArray.value = [...divisions]
        formState.district = { code, name }
        formState.street = {
          code: '', name: ''
        }
        break
      case 'street':
        formState.street = { code, name }
    }
  })
}

const midwareCity = (data: AddressModel) => {
  const { province, city, district } = data
  addressGetLocation(province.code).then(result => {
    const { divisions } = result
    cityArray.value = [...divisions]
  })
  addressGetLocation(city.code).then(result => {
    const { divisions } = result
    districtArray.value = [...divisions]
  })
  addressGetLocation(district.code).then(result => {
    const { divisions } = result
    streetArray.value = [...divisions]
  })
}

const defaultAddressOnChange = () => {
  formState.default = !!formState.default
}

const formSubmit = () => {
  formRef.value.validate().then(() => {
    if (props.mode === 'create') {
      addressAdd(formState).then(() => {
        message.success(t('orderInfo.AddressModel.addSuccess'))
        formCancel()
        emits('refresh')
      }).catch(() => {
        message.error(t('orderInfo.AddressModel.addFail'))
      })
    } else {
      addressUpdate(formState).then(() => {
        message.success(t('orderInfo.AddressModel.modifySuccess'))
        formCancel()
        emits('refresh')
      }).catch(() => {
        message.error(t('orderInfo.AddressModel.modifyFail'))
      })
    }
  })
}

const formCancel = () => {
  emits('modalController')
}

const testMobile = (str: string): boolean => {
  const regex = /^1[3456789]\d{9}$/
  if (!regex.test(str)) {
    return false
  } else {
    return true
  }
}


</script>

<style lang="less">
.address-model-itemLabel {
  padding-right: 120px;
}

.address-model-itemLabel2 {
  padding-right: 120px;
}

.address-modal {

  .ant-modal-footer {
    height: 75px;
    border-top: none;

    .footerLeft {
      display: inline-flex;
      float: left;
      margin-left: 20px;
    }

    .backBtn {
      background: #f6f6f6;
      color: #999999;
    }

    .ant-btn {
      width: 98px;
      height: 40px;
      border: none;
    }
  }
}
</style>
